import { Skeleton } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import {
  formatTimeByMode,
  useTimeDisplayMode,
  useWorkspaceTimeBounds,
} from "@/shared/components/visualization/WorkspaceCtx";
import { TimeBoundsSource } from "@/shared/state/visualization/schema";

import {
  ALL_COLUMNS_ID,
  type ColumnDefinition,
} from "../columnFilters/columnDefinition";
import type { FilterStageColorVars } from "../columnFilters/filterStageColors";
import { Log, Severity } from "../log";
import styles from "../LogPanel.module.css";
import {
  computeHighlights,
  type HighlightRange,
  type PreparedFilters,
} from "../search/highlightUtils";

import { HighlightedCell } from "./HighlightedCell";
import { toDisplayable } from "./toDisplayable";

function severityToBackgroundColor(
  selected: boolean,
  severity?: Severity,
): string {
  if (selected) {
    return "var(--table-row-selected)";
  }
  switch (severity) {
    case Severity.Emergency:
    case Severity.Alert:
    case Severity.Critical:
    case Severity.Error:
      return "var(--table-row-error)";
    case Severity.Warning:
      return "var(--table-row-warning)";
    default:
      return "inherit";
  }
}

interface Column extends ColumnDefinition {
  isLoading: boolean;
}

interface RowProps {
  columns: Column[];
  preparedFilters: PreparedFilters;
  dimmed: boolean;
  expanded: boolean;
  log: Log | undefined;
  onClick: (logTime: bigint, sequence: number) => void;
  selected: boolean;
  stageColors: FilterStageColorVars[];
  virtualIndex: number;
}

export const DEFAULT_LOG_LINE_HEIGHT = 35; // px

/**
 * Custom comparator for the memoized inner LogRow. Intentionally omits
 * `virtualIndex` so the row's cells do not re-render when the virtualizer
 * shifts its position. The outer (non-memoized) Row consumes `virtualIndex`
 * to set `data-index` on its wrapper; the inner LogRow does not read it.
 *
 * Also intentionally omits `dimmed`: dimming is purely a wrapper-level
 * className concern (see the outer Row's `styles.dimmed` toggle) and the
 * inner LogRow does not read it. Excluding it here avoids invalidating
 * memoized cells when only the dim state changes.
 */
function areLogRowPropsEqual(prev: RowProps, next: RowProps): boolean {
  return (
    prev.columns === next.columns &&
    prev.preparedFilters === next.preparedFilters &&
    prev.expanded === next.expanded &&
    prev.log === next.log &&
    prev.onClick === next.onClick &&
    prev.selected === next.selected &&
    prev.stageColors === next.stageColors
  );
}

const LogRow = React.memo(function LogRow({
  columns,
  preparedFilters,
  expanded,
  log,
  onClick,
  selected,
  stageColors,
}: RowProps) {
  const [timeDisplayMode] = useTimeDisplayMode();
  const timeBounds = useWorkspaceTimeBounds({
    timeBoundsSource: TimeBoundsSource.FilesInWorkspace,
  });

  const inlineStyles = React.useMemo(() => {
    const base = {
      "--hover-background-color": "var(--hover-background)",
      borderBottom: "var(--border-thin)",
    } as React.CSSProperties & Record<string, string>;

    if (expanded) {
      base["--row-height"] = "100%";
    }

    if (log !== undefined) {
      base["--background-color"] = severityToBackgroundColor(
        selected,
        log.severity,
      );
    }

    return base;
  }, [expanded, log, selected]);

  const onClickCb = React.useCallback(
    function onClickCb() {
      if (log === undefined) {
        return;
      }
      onClick(log.time, log.sequence);
    },
    [log, onClick],
  );

  return (
    <div className={styles.row} onClick={onClickCb} style={inlineStyles}>
      <p
        className={classNames(styles.cell, styles.fixedWidth, {
          [styles.expanded]: expanded,
        })}
      >
        {log !== undefined ? (
          formatTimeByMode(
            log.time,
            timeDisplayMode,
            timeBounds.earliest,
            false,
          )
        ) : (
          <Skeleton animation={false} height={20} />
        )}
      </p>
      {columns.map((column) => {
        let content: React.ReactNode;
        if (column.isLoading || log === undefined) {
          content = <Skeleton animation={false} height={20} />;
        } else {
          const text = toDisplayable(
            log.data[column.messagePathId],
            column.categories,
          );
          const highlights = hasHighlightsForColumn(
            column.messagePathId,
            preparedFilters,
          )
            ? computeHighlights(text, column.messagePathId, preparedFilters)
            : EMPTY_HIGHLIGHTS;
          content = (
            <HighlightedCell
              text={text}
              highlights={highlights}
              stageColors={stageColors}
            />
          );
        }
        return (
          <p
            key={column.messagePathId}
            className={classNames(
              "ph-no-capture",
              styles.cell,
              styles.flexibleWidth,
              {
                [styles.expanded]: expanded,
              },
            )}
          >
            {content}
          </p>
        );
      })}
    </div>
  );
}, areLogRowPropsEqual);

const EMPTY_HIGHLIGHTS: HighlightRange[] = [];

// Fast check: does this column (or the All Columns entry) have any prepared
// highlightable stages? Keyed off preparedFilters rather than filter state so
// that an empty preparedFilters (e.g. search toggled off) short-circuits
// computeHighlights entirely.
function hasHighlightsForColumn(
  columnId: string,
  preparedFilters: PreparedFilters,
): boolean {
  return preparedFilters.has(columnId) || preparedFilters.has(ALL_COLUMNS_ID);
}

export const Row = React.forwardRef<HTMLDivElement, RowProps>(function Row(
  props: RowProps,
  ref,
) {
  return (
    <div
      className={classNames(styles.rowContainer, {
        [styles.dimmed]: props.dimmed,
      })}
      // 2025-03-23 (GM)
      // While not well documented,
      // it appears (from reading source) the data-index attribute must match the virtualizer's index.
      // This may not match the *actual* index of the message in the topic (e.g., Message::sequence).
      data-index={props.virtualIndex}
      ref={ref}
    >
      <LogRow {...props} />
    </div>
  );
});
